import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../../context/WalletContext';
import { useContractServices } from '../../context/ContractServicesContext';
import { Shield, Settings, Clock, Zap, Coins, Lock, DollarSign, RefreshCw, AlertTriangle, Activity } from 'lucide-react';
import EmissionsAdminPanel from '../../components/admin/EmissionsAdminPanel';
import StakingAdminPanel from '../../components/admin/StakingAdminPanel';
import FeesAndTreasuryPanel from '../../components/admin/FeesAndTreasuryPanel';
import { toast } from 'react-toastify';

function AdminDashboard() {
  const { walletAddress, isConnected, chainId } = useWallet();
  const { services, isReady } = useContractServices();
  const navigate = useNavigate();
  
  const [activeSection, setActiveSection] = useState('emissions');
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [transactionCount, setTransactionCount] = useState(0);
  
  // Check if on testnet
  const isTestnet = chainId && chainId !== 10; // 10 is Optimism mainnet
  
  // Function to increment transaction count
  const incrementTransactionCount = () => {
    setTransactionCount(prev => prev + 1);
  };
  
  // Function to refresh data
  const refreshData = async () => {
    if (refreshing) return;
    
    try {
      setRefreshing(true);
      toast.info('Refreshing dashboard data...', {
        autoClose: 2000
      });
      
      // Increment refresh key to trigger useEffect hooks in child components
      setRefreshKey(prev => prev + 1);
      
      // Wait a moment for visual feedback
      await new Promise(resolve => setTimeout(resolve, 1000));
      
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };
  
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (isConnected && walletAddress && isReady) {
        try {
          const adminStatus = await services.admin.isAdmin(walletAddress);
          setIsAdmin(adminStatus);
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        } finally {
          setLoading(false);
        }
      } else {
        setIsAdmin(false);
        setLoading(false);
      }
    };
    
    checkAdminStatus();
  }, [walletAddress, isConnected, isReady, services]);
  
  // Redirect non-admins away from this page
  useEffect(() => {
    if (!loading && (!isConnected || !isAdmin)) {
      navigate('/');
    }
  }, [isAdmin, isConnected, navigate, loading]);
  
  if (loading) {
    return (
      <div className="min-h-screen p-8 flex justify-center items-center bg-gray-50">
        <div className="p-6 bg-white rounded-lg shadow-md text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Checking Access</h1>
          <p className="text-gray-600">
            Verifying your admin credentials...
          </p>
        </div>
      </div>
    );
  }
  
  if (!isConnected || !isAdmin) {
    return (
      <div className="min-h-screen p-8 flex justify-center items-center bg-gray-50">
        <div className="p-6 bg-white rounded-lg shadow-md text-center">
          <Shield className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Access Restricted</h1>
          <p className="text-gray-600">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }
  
  if (!isReady) {
    return (
      <div className="min-h-screen p-8 flex justify-center items-center bg-gray-50">
        <div className="p-6 bg-white rounded-lg shadow-md text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Loading Admin Dashboard</h1>
          <p className="text-gray-600">
            Please wait while we connect to the blockchain...
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {isTestnet && (
        <div className="bg-yellow-500 text-white p-2 text-center flex items-center justify-center space-x-2">
          <AlertTriangle size={16} />
          <p className="text-sm font-medium">You are connected to a test network. Actions taken here will not affect the mainnet.</p>
        </div>
      )}
    
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Shield className="h-8 w-8 text-indigo-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            
            {transactionCount > 0 && (
              <div className="ml-4 flex items-center bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
                <Activity size={14} className="mr-1" />
                {transactionCount} transaction{transactionCount !== 1 ? 's' : ''} submitted
              </div>
            )}
          </div>
          
          <button
            onClick={refreshData}
            disabled={refreshing}
            className="flex items-center px-4 py-2 bg-indigo-100 text-indigo-600 rounded-md hover:bg-indigo-200 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Navigation Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              className={`px-4 py-3 text-sm font-medium flex items-center ${
                activeSection === 'emissions'
                  ? 'border-b-2 border-indigo-500 text-indigo-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveSection('emissions')}
            >
              <Clock className="h-4 w-4 mr-2" />
              Emissions Management
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium flex items-center ${
                activeSection === 'staking'
                  ? 'border-b-2 border-indigo-500 text-indigo-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveSection('staking')}
            >
              <Lock className="h-4 w-4 mr-2" />
              Staking Configuration
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium flex items-center ${
                activeSection === 'fees'
                  ? 'border-b-2 border-indigo-500 text-indigo-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveSection('fees')}
            >
              <DollarSign className="h-4 w-4 mr-2" />
              Fees & Treasury
            </button>
          </div>
          
          {/* Content */}
          <div className="p-6">
            {activeSection === 'emissions' && (
              <EmissionsAdminPanel 
                adminService={services.admin} 
                refreshKey={refreshKey} 
                onTransactionSubmit={incrementTransactionCount} 
              />
            )}
            
            {activeSection === 'staking' && (
              <StakingAdminPanel 
                adminService={services.admin} 
                refreshKey={refreshKey} 
                onTransactionSubmit={incrementTransactionCount} 
              />
            )}
            
            {activeSection === 'fees' && (
              <FeesAndTreasuryPanel 
                adminService={services.admin} 
                refreshKey={refreshKey} 
                onTransactionSubmit={incrementTransactionCount} 
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard; 